import { StockResearchAdvicesAction } from './stock-research-advices.action';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { share, map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';
import { ErrorsType } from '../../enum/errors.type';
import { AppState } from '../../interfaces/app-state.interface';
import { BackendService } from '../../services/backend/backend.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { StocksExchangeWalletAction } from '../stocks-exchange-wallet/stocks-exchange-wallet.action';
import { SuitabilityProfilesEnum } from '../../enum/suitability/suitability-profiles.enum';
import { MarketAdviceEnum } from '../../enum/stock-advice/market-advice.enum';
import { ResearchActions } from '../research/research.action';
import { ResearchAdvice } from '../../models/research/research-advice.model';
import { OrderStatus } from '../../enum/orders/order-status.enum';
import { OrderType } from '../../enum/orders/order-type.enum';

@Injectable()
export class StockResearchAdvicesEffects {

    /**
     * Obtem todas as recomendações
     * @method getAdvices
     */
    getAdvices$ = createEffect(() => this.actions$
        .pipe(
			ofType(StockResearchAdvicesAction.getAdvices),
            withLatestFrom(this.store$.select(s => s), (action, state) => { 
                return {profile: state.suitability.profile, orders: state.stocksExchange.wallet.validStrategies};
            }),
            switchMap(({profile, orders}) => {
                return this.dataService.get<DefaultResponse>(`research/stockadvices`)
                    .pipe(
                        map((result) => {
                            if (result.ok) {

                                const ordersArr = this.filterUserInvestments(orders)

                                const filteredByUserInvestments = result.value
                                    .filter(item => !this.filterAdvices(item, ordersArr));

                                if ((profile && profile.profile == SuitabilityProfilesEnum.NoProfile) || !profile) {
                                    return { type: StockResearchAdvicesAction.getAdvicesSuccess, payload: filteredByUserInvestments.reverse() };
                                } else {
                                    if(profile.profile != SuitabilityProfilesEnum.Conservative) {
                                        const filteredByProfile = filteredByUserInvestments.filter(opportunity => {
                                            if(opportunity.market == MarketAdviceEnum.BMF && !opportunity.symbolID.match(/(WIN)|(WDO)/g)) {
                                                if(profile.profile == SuitabilityProfilesEnum.Bold) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            } else {
                                                return true;
                                            }
                                        });
                                        return { 
                                            type: StockResearchAdvicesAction.getAdvicesSuccess, 
                                            payload: filteredByProfile.reverse()
                                                .map((advice) => new ResearchAdvice(advice)),
                                        };
                                    } else {
                                        return { type: StockResearchAdvicesAction.getAdvicesSuccess, payload: [] };
                                    }

                                }
                            }
                            return { type: StockResearchAdvicesAction.getAdvicesError, payload: result.value };

                        }),
                        catchError(error => of({ type: StockResearchAdvicesAction.getAdvicesError, payload: error })),
                    )
            }),
            share(),
        ));

    /**
     * Obtem os detalhes de uma recomendção
     * @method getAdvice
     */
    getAdvice$ = createEffect(() => this.actions$
        .pipe(
            ofType(StockResearchAdvicesAction.getAdvice),
            switchMap((action: any) => {
                return this.dataService.get<DefaultResponse>(`research/stockadvice?idStockAdvice=${action.payload.id}`)
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: StockResearchAdvicesAction.getAdviceError, 
                                    payload: { type: ErrorsType.Warning, value: result.messages } 
                                }
                            }
                            if (result.hasError) {
                                return {
                                    type: StockResearchAdvicesAction.getAdviceError, 
                                    payload: { type: ErrorsType.Error, value: result.messages } 
                                };
                            }
                            if (result.ok) {
                                if (!result.hasValue) {
                                    return {
                                        type: StockResearchAdvicesAction.adviceNotFound, 
                                        payload: null,
                                    };
                                }

                                return { type: StockResearchAdvicesAction.getAdviceSuccess, payload: result.value };
                            }
                            if (result.ok && !result.hasValue) {
                                return {
                                    type: StockResearchAdvicesAction.getAdviceError, 
                                    payload: { type: ErrorsType.Error, value: result.messages } 
                                }
                            }

                            return { type: StockResearchAdvicesAction.getAdviceSuccess, payload: result }
                        }),
                        catchError((error) => of({ type: StockResearchAdvicesAction.getAdviceError, payload: error })),
                    );
            }),
            share(),
        ));

    constructor(
        private actions$: Actions,
        private dataService: BackendService,
        private store$: Store<AppState>,
    ) { }

    /**
     * Filtra as recomendações
     * @param item recomendação
     * @param investments Lista de investimentos do usuário
     */
    public filterAdvices(item: any, investments: Array<any>):boolean {
        return investments.some(investment => item.symbolID == investment.symbol && item.isDayTrade == investment.isDayTrade);
    }


    /**
     * Filtra os investimentos executados do usuário
     * @param investments Investimentos do usuário
     */
    private filterUserInvestments(investments: any): Array<any> {
        const investmentsArray = Object.keys(investments)
            .map(strategy => investments[strategy]);

        const investmentsWithoutExecutedLeg = investmentsArray.filter(strategy => {

            const legs: any[] = strategy.legs;
            const inputLeg = legs.find(leg => leg.internalOrderType == OrderType.Input);
            const stopGainLeg = legs.find(leg => leg.internalOrderType == OrderType.StopGain);
            const stopLossLeg = legs.find(leg => leg.internalOrderType == OrderType.StopLoss);

            if(!inputLeg || !stopGainLeg || !stopLossLeg) return false;


            if(inputLeg.status == OrderStatus.Filled || inputLeg.status == OrderStatus.PartiallyFilled) {
                if(stopGainLeg.status == OrderStatus.PendingTrigger && stopLossLeg.status == OrderStatus.PendingTrigger) {
                    return true;
                } else {
                    return false;
                }
            } else  {
                return false;
            }
        });

        return investmentsWithoutExecutedLeg;
    }
}
